<template>
	<v-container>
		<v-form ref="form" v-model="valid" @submit.prevent="sendCsrReport">
			<v-row class="contentRow">
				<!--'Question Number' icon-->
				<v-col cols="12">
					<h1>Complete this {{ name }}</h1>
					<v-text class="ml-1 pa-3" style="float: left; border-radius: 5px; background: #00800006;">
						The Orixe Sustainability survey is a comprehensive self-assessment tool that helps organizations measure their maturity in relation to the UN Global Compact's 10 principles. Through a questionnaire-based survey, you can evaluate your sustainability maturity across four key categories: Human Rights, Labour, Environment, and Anti-Corruption. This process provides an overall score for your organization and supply chain while encouraging continuous improvement through honest self-reflection on your status.</v-text>
				</v-col>
				<v-col cols="5" class="flex-grow-1">
					<v-alert dense v-if="unsaved" class="mt-5" type="warning">
						You have unsaved changes!
					</v-alert>
					<v-alert dense v-else class="mt-5" type="success">
						Please ensure to save the CSR report before exiting
					</v-alert>
				</v-col>
			</v-row>
			<div v-for="(question, index) in questions" v-bind:key="index">
				<v-row v-if="shouldDisplay(index)" class="mx-auto">
					<v-row no-gutters class="my-12" v-if="question.type == 'spacer'">
					</v-row>
					<v-row
						no-gutters
						v-else-if="question.type == 'title'"
						class="contentRow primary"
						:style="question.color ? `background-color: ${question.color} !important; border-color: ${question.color} !important` : ''"
					>
						<v-col cols="6" class="mr-auto white--text">
							<h2>{{ question.name }}</h2>
						</v-col>
						<!-- Details/hint expanding field -->
						<v-col cols="12">
							<v-btn
								class="my-3"
								dark
								text
								v-if="!!question.hint"
								@click.stop="toggleHint(index)"
							>
								{{ !!question.hintVisible ? "Hide" : "Show" }} Question Details
								<v-icon right>far fa-question-circle</v-icon>
							</v-btn>
						</v-col>

						<v-col cols="12" v-if="!!question.hintVisible">
							<v-card outlined class="my-3 white--text">
								<v-card-text style="white-space: pre-wrap">{{
									question.hint
								}}</v-card-text>
							</v-card>
						</v-col>
						<v-col cols="12">
							<p class="float-right white--text text-caption">
								<i>(Question ID {{ index }})</i>
							</p>
						</v-col>
					</v-row>
					<v-row
						no-gutters
						align="center"
						v-else-if="question.type == 'attachment'"
						class="contentRow secondary"
						dark
					>
						<v-col cols="6" class="mr-auto white--text">
							<h2>
								<v-icon dark left>fas fa-file-download</v-icon
								>{{ question.name }}
							</h2>
						</v-col>
						<!--'File uplad' text field-->
						<v-col cols="12" class="white--text">
							<p class="mt-2">
								<i>The CSR Report includes file(s) for you to download:</i>
							</p>
							<file-uploader
								:value="question.fileID"
								:readonly="true"
								:disabled="true"
								:dark="true"
								:name="question.name"
							/>
						</v-col>
						<!-- Details/hint expanding field -->
						<v-col cols="12">
							<v-btn
								class="my-3"
								dark
								text
								v-if="!!question.hint"
								@click.stop="toggleHint(index)"
							>
								{{ !!question.hintVisible ? "Hide" : "Show" }} Question Details
								<v-icon right>far fa-question-circle</v-icon>
							</v-btn>
						</v-col>
						<v-col cols="12" v-if="!!question.hintVisible">
							<v-card outlined class="my-3 white--text">
								<v-card-text style="white-space: pre-wrap">{{
									question.hint
								}}</v-card-text>
							</v-card>
						</v-col>
						<v-col cols="12">
							<p class="float-right grey--text text-caption">
								<i>(Question ID {{ index }})</i>
							</p>
						</v-col>
					</v-row>
					<v-row class="contentRow" align="center" no-gutters v-else>
						<!--'Question Number' icon-->
						<!-- <v-col cols="1" v-if="question.type != 'title'">
						<v-avatar dense tile color="secondary">
							<h3 class="white--text">#{{index}}</h3>
						</v-avatar>
						</v-col>-->
						<!--'Title Name' label-->
						<!--'Question Name' label-->
						<v-col cols="12">
							<h3 class="my-3">
								{{ question.name
								}}<sup v-if="!!question.required"
									><v-icon color="error" x-small right
										>fas fa-asterisk</v-icon
									></sup
								>
							</h3>
						</v-col>
						<!--'Question Name' text field-->
						<v-col cols="12" v-if="question.type == 'singleanswer'">
							<v-text-field
								dense
								align="center"
								label="Answer"
								hide-details="auto"
								placeholder="Enter your answer:"
								:rules="question.required ? notEmptyRules : []"
								outlined
								v-model="answers[index]"
								class="my-3"
							></v-text-field>
						</v-col>
						<!--'Dependency Question' text field-->
						<!--'Doesn't show entire text! Should be updated'-->
						<v-col cols="12" v-if="question.type == 'multiplechoice'">
							<v-select
								dense
								outlined
								v-model="answers[index]"
								:items="question.possibilities"
								menu-props="auto"
								label="Please Choose"
								hide-details="auto"
								single-line
								:rules="question.required ? notEmptyRules : []"
								class="my-3"
								@change="updateAnswerNumber(index, $event)"
								:value="answers[index] !== null ? question.possibilities[answers[index]] : null"
							></v-select>
						</v-col>
						<!--'Dependency Question' text field-->
						<v-col cols="12" v-if="question.type == 'multipleanswers'">
							<v-select
								dense
								outlined
								multiple
								v-model="answers[index]"
								:items="question.possibilities"
								menu-props="auto"
								label="Please Choose"
								hide-details="auto"
								single-line
								:rules="question.required ? notEmptyRules : []"
								class="my-3"
							></v-select>
						</v-col>
						<!--'File uplad' text field-->
						<v-col cols="12" v-if="question.type == 'file'">
							<file-uploader v-model="answers[index]" />
						</v-col>
						<!-- Details/hint expanding field -->
						<v-col cols="12">
							<v-btn
								class="my-3"
								text
								v-if="!!question.hint"
								@click.stop="toggleHint(index)"
							>
								{{ !!question.hintVisible ? "Hide" : "Show" }} Question Details
								<v-icon right>far fa-question-circle</v-icon>
							</v-btn>
						</v-col>

						<v-col cols="12" v-if="!!question.hintVisible">
							<v-card outlined class="my-3">
								<v-card-text style="white-space: pre-wrap">{{
									question.hint
								}}</v-card-text>
							</v-card>
						</v-col>
						<v-col cols="12">
							<p class="float-right grey--text text-caption">
								<i>(Question ID {{ index }})</i>
							</p>
						</v-col>
					</v-row>
				</v-row>
			</div>
			<v-row
				no-gutters
				class="contentRow"
				style="border: 2px solid #4CAF50; /*#3d2645*/"
			>
				<v-col cols="12" class="mr-auto">
					<h2>Ready to submit?</h2>
					<p>
						Note: You will not be able to change your answers at a later time.
					</p>
					<v-btn
						color="success"
						:disabled="!valid || preview"
						@click="openDialog"
						type="submit"
						align="center"
						class="mt-4"
					>
						Submit
						<v-icon right>fas fa-paper-plane</v-icon>
					</v-btn>
				</v-col>
			</v-row>
			<v-tooltip left v-if="!preview">
				<template v-slot:activator="{ on, attrs }">
					<v-fab-transition>
						<v-btn
							color="success"
							x-large
							rounded
							dark
							bottom
							right
							fixed
							:style="
								unsaved
									? 'border: 2pt outset black; border-color: var(--v-warning-base) !important; '
									: ''
							"
							v-on="on"
							v-bind="attrs"
							@click="saveCsrReport"
						>
							<v-icon left>fas fa-save</v-icon>Save
						</v-btn>
					</v-fab-transition>
				</template>
				<span
					>Click to save{{
						unsaved ? " - You have unsaved changes!" : ""
					}}</span
				>
			</v-tooltip>
		</v-form>
		<v-dialog v-model="dialog" max-width="600px">
				<v-card>
					<v-card-title class="headline">Submit CSR Report</v-card-title>
					<v-card-text>
						Are you sure you want to submit the survey? Please confirm that your answers are accurate before proceeding. Your responses will impact your score, which cannot be changed for the next 12 months.
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn color=primary text @click="dialog = false">Go back to the answers</v-btn>
						<v-btn color=primary text @click="submitForm">Submit the survey</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
	</v-container>
</template>

<script>
//TODO Replace Audit API with CSR API when done
import LoadingState from "../consts/LoadingState"
import FilesAPI from "../api/files/files"
import FileUploaderVue from "../components/FileUploader.vue"
export default {
	name: "FormInput",
	components: {
		"file-uploader": FileUploaderVue
	},
	data() {
		return {
			sheet: false,
			notEmptyRules: [v => !!v || "This field cannot be empty"],
			valid: false,
			answers: [],
			files: [],
			dialog: false,
			answernumbers: [null,null],
		}
	},
	props: {
		preview: Boolean,
		previewID: Number,
		questions: Array,
		savedAnswers: Array,
		fileID: Number,
		id: Number,
		name: String,
		receiveanswernumbers: Array

	},
	mounted() {
		if (Array.isArray(this.receiveanswernumbers) && this.receiveanswernumbers.length) {
			this.answernumbers = this.receiveanswernumbers
		} else {
			// Set a default empty array if undefined or not valid
			this.answernumbers = Array(this.questions.length).fill(null)
		}
		const filteredAnswers = this.receiveanswernumbers.map((value, index) => {
			if (value === null) {
				return null // Keep `null` values as-is
			}
			const question = this.questions[index] // Get the corresponding question
			if (question && question.possibilities && question.possibilities[value]) {
				return question.possibilities[value] // Map `sss` value to the corresponding answer
			}
			return null // Fallback if question or possibility is invalid
		})
		this.answers = filteredAnswers || []
	},
	computed: {
		unsaved() {
			if (this.preview) {
				return false
			}
			let equal =
				this.answers.length == this.savedAnswers.length &&
				this.answers.every((e, i) => {
					return e == this.savedAnswers[i]
				})
			return !equal
		}
	},
	watch: {
		savedAnswers(newVal) {
			this.answers = JSON.parse(JSON.stringify(newVal))
		},
		answers() {
			return
		}
	},
	methods: {
		fileChanged(index) {
			FilesAPI.createFile(this.files[index].name, this.files[index])
				.then(file => {
					this.$set(this.answers, index, file.id)
				})
				.catch(err => {
					this.files[index] = null
					console.error(err)
				})
		},
		removeFile(index) {
			FilesAPI.deleteFile(this.answers[index])
				.then(() => {
					this.$set(this.answers, index, null)
				})
				.catch(err => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						err,
						{ root: true }
					)
				})
		},
		downloadFile(fileID, name) {
			FilesAPI.download("" + fileID, name)
		},
		shouldDisplay(index) {
			if (!this.questions[index].dependencyQuestionID) {
				return true
			}
			if (!this.answers[this.questions[index].dependencyQuestionID]) {
				return false
			}
			if (this.questions[index].dependencyQuestionValue.includes(this.answers[this.questions[index].dependencyQuestionID])) {
				return true
			}

			if (typeof this.answers[this.questions[index].dependencyQuestionID] == "object") {
				for (let element of this.answers[this.questions[index].dependencyQuestionID]) {
					if (this.questions[index].dependencyQuestionValue.includes(element)) {
						return true
					}
				}
			}
			return false
		},
		openDialog() {
			this.dialog = true
		},
		// Method to handle the actual submission after confirmation
		submitForm() {
			this.dialog = false // Close the dialog
			this.$emit("submitForm", this.answers)
		},
		saveCsrReport() {
			this.$emit("save", this.answers)
		},
		toggleHint(index) {
			let source = { hintVisible: !this.questions[index].hintVisible }
			let newQuestion = Object.assign({}, this.questions[index], source)
			this.$set(this.questions, index, newQuestion)
		},
		updateAnswerNumber(index, selectedValue) {
			// Get the index of the selected value in the possibilities array
			const question = this.questions[index]
			const selectedIndex = question.possibilities.indexOf(selectedValue)

			// Update answernumbers with the selected index, keeping unselected items as null
			this.$set(this.answernumbers, index, selectedIndex !== -1 ? selectedIndex : null)

			// Ensure the array always has the correct length (e.g., 22)
			while (this.answernumbers.length < this.questions.length) {
				this.answernumbers.push(null)
			}
			this.$emit("updateAnswerNumbers", [...this.answernumbers])
		},
	}
}
</script>
